import { ref, computed, provide } from 'vue'

import { sideMenuDisplayModeInjectionKey } from './injectionKey'

import type { watch as _watch } from 'vue'
import type {
  GetBuyerPageOptionsResponse,
} from '@/types/api/customer/BuyerPageMeta/index.ts'

import { useRoute } from '#app/composables/router'
import { SideMenuDisplayMode } from '@/constants/enums/SideMenuDisplayMode'
import { useResizeObserver } from '@/composables/useResizeObserver'
import { getBuyerPageOptions } from '@/composables/api/customer/BuyerPageMeta'

/**
 * Constants
 */
const SIDE_MENU_WIDTH = 240
const CONTENT_MIN_WIDTH = 960
const CONTENT_MIN_WIDTH_ESG_DATA_DETAIL_AND_EDIT = 1200

/**
 * Export
 */
export const useCustomerSideMenu = (watch: typeof _watch) => {
  const route = useRoute()

  /**
   * Refs
   */
  // TODO: このモードの判定に利用しているのは画面全体の幅ではなく、対象エレメント(contentElement)の幅であることに注意。
  //  そのため、一度描画されないと（初期表示時に）判定ができず、初期表示時のチラつきが発生する。
  const sideMenuDisplayMode = ref<SideMenuDisplayMode>(
    SideMenuDisplayMode.ALWAYS,
  )
  const isOpenedSideMenu = ref<boolean>(false)
  const contentElement = ref<HTMLElement | undefined>()
  const buyerPageOptions = ref<GetBuyerPageOptionsResponse>({
    esgSupplierRegistTemplateCount: 0,
    hasPublicImprovementPlan: false,
    hasEsgDataSharedAnswers: false,
  })

  /**
   * Computed
   */
  const isDisplayedSideMenu = computed<boolean>(() => {
    return (
      sideMenuDisplayMode.value === SideMenuDisplayMode.ALWAYS
      || isOpenedSideMenu.value
    )
  })
  const isEsgDataSharePreviewOrEdit = computed(() => {
    const path = route.path
    return path.includes('/datashare/detail') || path.includes('/datashare/edit')
  })

  /**
   * Methods
   */
  const fetchBuyerPageOptions = async () => {
    const response = await getBuyerPageOptions()
    if (response.status.value === 'success') {
      buyerPageOptions.value = response.data.value
    }
    else {
      // TODO: エラーハンドリング
      window.alert(response.error.value?.data.message)
    }
  }

  /**
   * Composables
   */
  useResizeObserver(contentElement, (entries) => {
    const entry = entries[0]
    if (!entry) {
      return
    }

    const currentContentWidth = entry.borderBoxSize?.[0]?.inlineSize
    const contentMinWidth = isEsgDataSharePreviewOrEdit.value ? CONTENT_MIN_WIDTH_ESG_DATA_DETAIL_AND_EDIT : CONTENT_MIN_WIDTH

    if (
      sideMenuDisplayMode.value === SideMenuDisplayMode.ALWAYS
      && currentContentWidth < contentMinWidth
    ) {
      sideMenuDisplayMode.value = SideMenuDisplayMode.SWITCH
    }

    if (
      sideMenuDisplayMode.value === SideMenuDisplayMode.SWITCH
      && currentContentWidth >= contentMinWidth + SIDE_MENU_WIDTH
    ) {
      sideMenuDisplayMode.value = SideMenuDisplayMode.ALWAYS
    }
  })

  /**
   * Watchers
   */
  watch(
    () => sideMenuDisplayMode.value,
    () => {
      isOpenedSideMenu.value = false
    },
  )

  /**
   * Provide
   */
  provide(sideMenuDisplayModeInjectionKey, sideMenuDisplayMode)

  return {
    sideMenuDisplayMode,
    isOpenedSideMenu,
    contentElement,
    isDisplayedSideMenu,
    buyerPageOptions,
    fetchBuyerPageOptions,
  }
}
